<script setup lang="ts">
import { computed, ref } from "vue";
import { formatAvailability, formatGrouped, MISSING } from "../format";
import { logDomain, logPosition, logTicks } from "../series";
import type { EndpointSnapshot } from "../types/api";
import { GROUP_LABELS, type EndpointGroup, type EndpointInsight } from "../weather";
import AppIcon from "./AppIcon.vue";
import Collapse from "./Collapse.vue";
import EndpointDetail from "./EndpointDetail.vue";

const props = defineProps<{
  endpoints: EndpointSnapshot[];
  insights: EndpointInsight[];
}>();

const openId = ref<string | null>(null);

function hostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

const rows = computed(() =>
  props.endpoints.map((endpoint, index) => ({
    endpoint,
    insight: props.insights[index],
    host: hostname(endpoint.url)
  }))
);

/** Rows in configuration order, split where the group changes. */
const groups = computed(() => {
  const result: Array<{ group: EndpointGroup; rows: typeof rows.value }> = [];
  for (const row of rows.value) {
    const current = result.find((item) => item.group === row.insight.group);
    if (current) current.rows.push(row);
    else result.push({ group: row.insight.group, rows: [row] });
  }
  return result;
});

/**
 * One logarithmic latency axis for all rows. It is framed by what is usual (P10 to P98),
 * not by the newest probe, so a single spike slides a dot to the edge instead of
 * rescaling the whole list. An endpoint without a baseline yet lends its current value.
 */
const domain = computed<[number, number] | null>(() => {
  let low = Infinity;
  let high = 0;
  for (const item of props.insights) {
    const floor = item.p10 ?? item.latency;
    const ceiling = item.p98 ?? item.p90 ?? item.latency;
    if (floor != null && floor > 0) low = Math.min(low, floor);
    if (ceiling != null) high = Math.max(high, ceiling);
  }
  if (!Number.isFinite(low) || high <= 0) return null;
  return logDomain(low, Math.max(high, low * 2));
});

const percent = (value: number) => `${(logPosition(value, domain.value as [number, number]) * 100).toFixed(2)}%`;

const axis = computed(() => {
  if (!domain.value) return { guides: [], labels: [] };
  const ticks = logTicks(domain.value);
  const majors = ticks.filter((tick) => tick.major);
  const labelled = majors.length >= 2 ? majors : [ticks[0], ticks[ticks.length - 1]];
  const text = (value: number) => (value >= 1000 ? `${value / 1000} s` : `${value} ms`);
  return {
    guides: ticks.map((tick) => ({ left: percent(tick.value), major: tick.major })),
    labels: labelled.map((tick) => ({ left: percent(tick.value), text: text(tick.value) }))
  };
});

function band(insight: EndpointInsight) {
  if (!domain.value || insight.p10 == null || insight.p90 == null) return null;
  const start = logPosition(insight.p10, domain.value);
  const end = logPosition(insight.p90, domain.value);
  return { left: `${(start * 100).toFixed(2)}%`, width: `${((end - start) * 100).toFixed(2)}%` };
}

function dotClass(insight: EndpointInsight): string {
  if (insight.status === "ok") return "dot--ok";
  if (insight.status === "slow") return "dot--warn";
  if (insight.status === "unknown") return "";
  return "dot--bad";
}

function toggle(id: string) {
  openId.value = openId.value === id ? null : id;
}
</script>

<template>
  <div class="board">
    <div class="board__head" aria-hidden="true">
      <span class="board__col-name">
        <i class="key key--band"></i>24 小时常见范围
        <i class="key key--dot"></i>当前
      </span>
      <span class="board__axis">
        <span v-for="label in axis.labels" :key="label.text" :style="{ left: label.left }">{{ label.text }}</span>
      </span>
      <span class="board__col-avail">可用率</span>
      <span class="board__col-latency">延迟</span>
    </div>

    <template v-for="section in groups" :key="section.group">
      <h3 v-if="groups.length > 1" class="board__group">{{ GROUP_LABELS[section.group] }}</h3>
      <ul class="board__list">
        <li
          v-for="{ endpoint, insight, host } in section.rows"
          :key="endpoint.id"
          class="row"
          :class="{ 'row--open': openId === endpoint.id }"
        >
          <button
            class="row__head"
            type="button"
            :aria-expanded="openId === endpoint.id"
            :aria-controls="`endpoint-${endpoint.id}`"
            @click="toggle(endpoint.id)"
          >
            <span class="dot row__dot" :class="dotClass(insight)" aria-hidden="true"></span>
            <span class="row__name">
              <strong>{{ endpoint.name }}</strong>
              <small :class="{ 'row__host--swap': insight.availability != null && insight.availability < 99 }">
                <span class="row__host">{{ host }}</span>
                <span class="row__name-avail num">24 小时可用率 {{ formatAvailability(insight.availability) }}</span>
              </small>
            </span>

            <span class="row__plot" aria-hidden="true">
              <i
                v-for="(guide, index) in axis.guides"
                :key="index"
                class="row__guide"
                :class="{ 'row__guide--major': guide.major }"
                :style="{ left: guide.left }"
              ></i>
              <i v-if="band(insight)" class="row__band" :style="band(insight) ?? undefined"></i>
              <i
                v-if="domain && insight.latency != null"
                class="row__now"
                :class="{ 'row__now--slow': insight.status === 'slow' }"
                :style="{ left: percent(insight.latency) }"
              ></i>
            </span>

            <span
              class="row__avail num"
              :class="{ 'row__avail--low': insight.availability != null && insight.availability < 99 }"
            >
              <span class="visually-hidden">24 小时可用率</span>
              {{ formatAvailability(insight.availability) }}
            </span>

            <span class="row__latency">
              <template v-if="insight.latency != null">
                <span class="visually-hidden">当前延迟</span>
                <strong class="num">{{ formatGrouped(insight.latency) }}</strong><span class="unit">ms</span>
              </template>
              <strong v-else-if="insight.status === 'failed' || insight.status === 'down'" class="row__word">
                不可达
              </strong>
              <strong v-else>{{ MISSING }}</strong>
              <small v-if="insight.status === 'slow'" class="num">
                <span class="visually-hidden">比平时慢，</span>平时 {{ formatGrouped(insight.median) }} ms
              </small>
              <small v-else-if="insight.status === 'down'">已 {{ insight.failingMinutes }} 分钟</small>
            </span>

            <AppIcon class="row__caret" name="chevron-right" :size="14" />
          </button>

          <Collapse :open="openId === endpoint.id">
            <EndpointDetail :id="`endpoint-${endpoint.id}`" :endpoint="endpoint" />
          </Collapse>
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped>
.board {
  --columns: 0.5rem minmax(8.5rem, 14rem) minmax(7rem, 1fr) 4rem 6.75rem 0.875rem;
  --column-gap: 1rem;
}

.board__head,
.row__head {
  display: grid;
  grid-template-columns: var(--columns);
  column-gap: var(--column-gap);
  align-items: center;
}

.board__head { color: var(--ink-3); font-size: 0.75rem; line-height: 1.4; }
.board__col-name { grid-column: 1 / span 2; display: flex; gap: 0.4rem; align-items: center; white-space: nowrap; }
.board__axis { position: relative; grid-column: 3; height: 1rem; }
.board__axis span { position: absolute; top: 0; transform: translateX(-50%); white-space: nowrap; font-variant-numeric: tabular-nums; }
.board__col-avail { grid-column: 4; text-align: right; }
.board__col-latency { grid-column: 5; text-align: right; }
.key { display: inline-block; flex: 0 0 auto; }
.key--band { width: 1rem; height: 0.375rem; border-radius: 999px; background: var(--accent-soft); }
.key--dot { width: 0.5rem; height: 0.5rem; margin-left: 0.5rem; border-radius: 50%; background: var(--accent); }

.board__group {
  margin: 1.5rem 0 0.25rem;
  color: var(--ink-3);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.08em;
}
.board__head + .board__group { margin-top: 0.75rem; }
.board__list { margin: 0; padding: 0; list-style: none; }

/* The hover wash reaches a little past the text column on both sides. */
.row__head {
  width: calc(100% + 1.5rem);
  min-height: 3.5rem;
  margin-inline: -0.75rem;
  padding-inline: 0.75rem;
  border-radius: var(--radius);
  text-align: start;
}
.row__head:hover,
.row--open .row__head { background: var(--wash); }

.row__name { display: grid; min-width: 0; }
.row__name strong { overflow: hidden; font-size: 0.9375rem; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.row__name small { overflow: hidden; color: var(--ink-3); font-size: 0.8125rem; text-overflow: ellipsis; white-space: nowrap; }

/* The availability column is dropped on narrow screens; a poor figure moves under the name. */
.row__name-avail { display: none; color: var(--ink-2); }
.row__plot { position: relative; align-self: stretch; min-height: 3.5rem; }
.row__guide { position: absolute; top: 0; bottom: 0; width: 1px; background: var(--grid); opacity: 0.55; }
.row__guide--major { opacity: 1; }
.row__band {
  position: absolute;
  top: 50%;
  height: 0.5rem;
  min-width: 0.5rem;
  border-radius: 999px;
  background: var(--accent-soft);
  transform: translateY(-50%);
}
.row__now {
  position: absolute;
  top: 50%;
  width: 0.75rem;
  height: 0.75rem;
  border: 2px solid var(--bg);
  border-radius: 50%;
  background: var(--accent);
  transform: translate(-50%, -50%);
}
.row--open .row__now,
.row__head:hover .row__now { border-color: color-mix(in srgb, var(--bg) 92%, var(--ink)); }
.row__now--slow { background: var(--warn); }

.row__avail { color: var(--ink-3); font-size: 0.8125rem; text-align: right; }
.row__avail--low { color: var(--ink); font-weight: 600; }

.row__latency { display: grid; grid-template-columns: 1fr auto; align-items: baseline; justify-items: end; text-align: right; }
.row__latency strong { font-size: 1.0625rem; font-weight: 500; letter-spacing: -0.01em; }
.row__latency .row__word { grid-column: 1 / -1; font-size: 0.9375rem; }
.row__latency small { grid-column: 1 / -1; color: var(--ink-3); font-size: 0.75rem; white-space: nowrap; }

.row__caret { color: var(--ink-3); }
.row--open .row__caret { transform: rotate(90deg); }

@media (prefers-reduced-motion: no-preference) {
  .row__band { transition: left 600ms cubic-bezier(0.2, 0.7, 0.2, 1), width 600ms cubic-bezier(0.2, 0.7, 0.2, 1); animation: band-in 500ms ease both; }
  .row__now { transition: left 600ms cubic-bezier(0.2, 0.7, 0.2, 1), background-color 200ms ease; }
  .row__guide, .board__axis span { transition: left 600ms cubic-bezier(0.2, 0.7, 0.2, 1); }
  .row__caret { transition: transform 200ms ease; }
}
@keyframes band-in { from { opacity: 0; } }

@media (max-width: 640px) {
  .board { --columns: 0.5rem minmax(0, 1fr) auto 0.875rem; --column-gap: 0.75rem; }
  .board__head { grid-template-columns: minmax(0, 1fr); row-gap: 0.5rem; }
  .board__col-name { grid-column: 1; }
  .board__axis { grid-column: 1; margin-left: 1.25rem; margin-right: 1.625rem; }
  .board__col-avail, .board__col-latency { display: none; }
  .row__head { row-gap: 0; padding-block: 0.7rem 0.25rem; }
  .row__plot { grid-column: 2 / 4; grid-row: 2; min-height: 1.75rem; }
  .row__avail { display: none; }
  .row__host--swap .row__host { display: none; }
  .row__host--swap .row__name-avail { display: inline; }
  .row__latency { grid-column: 3; grid-row: 1; }
  .row__caret { grid-column: 4; grid-row: 1; }
}
</style>
